import store from '@/store'

const ACCOUNT_ROLE = Object.freeze({
  NONE: 0,
  OWNER: 1,
  ADMIN: 2,
  CREATOR: 3,
  ANALYST: 4,
  CLIENT: 5,
})

const PLATFORM_ROLE = Object.freeze({
  USER: 0,
  SUPERADMIN: 1,
  ADMIN: 2,
  ADVERTISER_MANAGER: 3,
  PROPERTY_MANAGER: 4,
  FINANCE_MANAGER: 5,
  DEVELOPER: 6,
})

const accountRoleIsAtLeast = role => {
  const { accountRole } = store.getters['verticalMenu/accountContext']

  switch (role) {
    case ACCOUNT_ROLE.OWNER:
      return accountRole === ACCOUNT_ROLE.OWNER
    case ACCOUNT_ROLE.ADMIN:
      return accountRole === ACCOUNT_ROLE.OWNER || accountRole === ACCOUNT_ROLE.ADMIN
    case ACCOUNT_ROLE.CREATOR:
      return accountRole === ACCOUNT_ROLE.OWNER || accountRole === ACCOUNT_ROLE.ADMIN || accountRole === ACCOUNT_ROLE.CREATOR
    case ACCOUNT_ROLE.ANALYST:
      return accountRole === ACCOUNT_ROLE.OWNER || accountRole === ACCOUNT_ROLE.ADMIN || accountRole === ACCOUNT_ROLE.CREATOR || accountRole === ACCOUNT_ROLE.ANALYST
    case ACCOUNT_ROLE.CLIENT:
      return accountRole === ACCOUNT_ROLE.OWNER || accountRole === ACCOUNT_ROLE.ADMIN || accountRole === ACCOUNT_ROLE.CREATOR || accountRole === ACCOUNT_ROLE.CLIENT
    case ACCOUNT_ROLE.NONE:
      return accountRole === ACCOUNT_ROLE.NONE
    default:
      return false
  }
}

const platformRoleIsAtLeast = role => {
  const { platformRole } = store.getters['verticalMenu/accountContext']

  switch (role) {
    case PLATFORM_ROLE.DEVELOPER:
      return platformRole === PLATFORM_ROLE.DEVELOPER
    case PLATFORM_ROLE.SUPERADMIN:
      return platformRole === PLATFORM_ROLE.DEVELOPER || platformRole === PLATFORM_ROLE.SUPERADMIN
    case PLATFORM_ROLE.ADMIN:
      return platformRole === PLATFORM_ROLE.DEVELOPER || platformRole === PLATFORM_ROLE.SUPERADMIN || platformRole === PLATFORM_ROLE.ADMIN
    case PLATFORM_ROLE.ADVERTISER_MANAGER:
      return platformRole === PLATFORM_ROLE.DEVELOPER || platformRole === PLATFORM_ROLE.SUPERADMIN || platformRole === PLATFORM_ROLE.ADMIN || platformRole === PLATFORM_ROLE.ADVERTISER_MANAGER
    case PLATFORM_ROLE.PROPERTY_MANAGER:
      return platformRole === PLATFORM_ROLE.DEVELOPER || platformRole === PLATFORM_ROLE.SUPERADMIN || platformRole === PLATFORM_ROLE.ADMIN || platformRole === PLATFORM_ROLE.PROPERTY_MANAGER
    case PLATFORM_ROLE.FINANCE_MANAGER:
      return platformRole === PLATFORM_ROLE.DEVELOPER || platformRole === PLATFORM_ROLE.SUPERADMIN || platformRole === PLATFORM_ROLE.ADMIN || platformRole === PLATFORM_ROLE.FINANCE_MANAGER
    case PLATFORM_ROLE.USER:
      return platformRole === PLATFORM_ROLE.USER
    default:
      return false
  }
}

export const PlatformAccessManager = {
  canManageConfiguration() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER)
  },
  canManageIndustries() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER)
  },
  canAddTranslationKeys() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER)
  },
  canManageDebuggers() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER)
  },
  canManageInfos() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER)
  },
  canManageScoring() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManageAdTypes() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManagePlatformUsers() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManageEmails() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManageTargetingProperties() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManageBidStrategyMappings() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canSeeYoutubeVideoId() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManageAudienceAccess() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canManageAllAccounts() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canManageLanguages() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canSeeAllCatalogs() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canSeeAudiences() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canSeeDashboard() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canSeePrebidDashboard() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canCreateDefaultTablePresets() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canEditDefaultPresets() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canDeleteDefaultPresets() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canManageDeposits() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canPreviewAudienceQuery() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canSeeGallery() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canManageSurveys() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  CanSeeErrorDashboard() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN)
  },
  canManageCampaigns() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER)
  },
  canManageAdGroups() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER)
  },
  canManageAds() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER)
  },
  canManageAudiences() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER)
  },
  canSeeAccountStatistics() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER)
  },
  canSeeAllProperties() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER)
  },
  canManageProperties() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER)
  },
  canSeeAllPlacements() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER)
  },
  canSeePropertyEarnings() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER)
  },
  canSeePropertyForecastings() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER)
  },
  canManageTransactions() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.FINANCE_MANAGER)
  },
  canManageWithdraws() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.FINANCE_MANAGER)
  },
  canMarkWithdrawsAsPaid() {
    const { platformRole } = store.getters['verticalMenu/accountContext']
    return platformRole === PLATFORM_ROLE.SUPERADMIN || platformRole === PLATFORM_ROLE.FINANCE_MANAGER
  },
  canSeeAllAccounts() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER) || platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER) || platformRoleIsAtLeast(PLATFORM_ROLE.FINANCE_MANAGER)
  },
  hasAccessInAdmin() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.ADVERTISER_MANAGER) || platformRoleIsAtLeast(PLATFORM_ROLE.FINANCE_MANAGER) || platformRoleIsAtLeast(PLATFORM_ROLE.PROPERTY_MANAGER)
  },
  canSeeDailyCosts() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER) || platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN) || platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
  canSeePerfomanceReport() {
    return platformRoleIsAtLeast(PLATFORM_ROLE.DEVELOPER) || platformRoleIsAtLeast(PLATFORM_ROLE.ADMIN) || platformRoleIsAtLeast(PLATFORM_ROLE.SUPERADMIN)
  },
}

export const AccountAccessManager = {
  hasAccessInAccount() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeeAllAccounts() },
  canManageProperties() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) || PlatformAccessManager.canManageProperties() },
  canManageAccount() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) || PlatformAccessManager.canManageAllAccounts() },
  canManageDeposits() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) || PlatformAccessManager.canManageDeposits() },
  canManageUsers() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) || PlatformAccessManager.canManageAllAccounts() },
  canManageCatalogs() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) || PlatformAccessManager.canSeeAllCatalogs() },
  canSeeAccountStatistics() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeeAccountStatistics() },
  canManageCampaigns() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || PlatformAccessManager.canManageCampaigns() },
  canManageAdGroups() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || PlatformAccessManager.canManageAdGroups() },
  canManageAds() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || PlatformAccessManager.canManageAds() },
  canSeeEarnings() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeePropertyEarnings() },
  canSeeForecastings() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeePropertyForecastings() },
  canSeeProperties() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeeAllProperties() },
  canSeeCatalogs() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) || PlatformAccessManager.canSeeAllCatalogs() },
  canSeeAudiences() { return PlatformAccessManager.canManageAudiences() },
  canSeeBalance() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeeAllAccounts() },
  canSeeAccountInfo() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || accountRoleIsAtLeast(ACCOUNT_ROLE.ANALYST) || PlatformAccessManager.canSeeAllAccounts() },
  canPreviewAudienceQuery() { return PlatformAccessManager.canPreviewAudienceQuery() },
  canManageSurveys() { return PlatformAccessManager.canManageSurveys() },
  canSeeGallery() { return accountRoleIsAtLeast(ACCOUNT_ROLE.CLIENT) || PlatformAccessManager.canSeeGallery() },
  canSeeAPIKeys() { return accountRoleIsAtLeast(ACCOUNT_ROLE.ADMIN) },
}

export const sidebarAccessManager = route => {
  const { hasActiveProperties } = store.getters['verticalMenu/accountContext']
  const { clientConfiguration } = store.getters.configuration
  const accountContextInfo = JSON.parse(JSON.stringify(store.getters['verticalMenu/accountContext']))
  if (Object.keys(accountContextInfo).length === 0) { return false }

  switch (route) {
    // for navigation group
    case 'ClientPortal.SideMenu.Create':
      return AccountAccessManager.canManageCampaigns()
    case 'Admin.SideMenu.AdDebugger':
      return PlatformAccessManager.canManageDebuggers()
    case 'Admin.SideMenu.AdTypes':
      return PlatformAccessManager.canManageAdTypes()
    case 'Admin.SideMenu.Configuration':
      return PlatformAccessManager.canManageLanguages()
    // for navigation items
    case 'statistics':
      return AccountAccessManager.canSeeAccountStatistics()
    case 'campaign':
      return AccountAccessManager.canManageCampaigns()
    case 'ad-group':
      return AccountAccessManager.canManageAdGroups()
    case 'ad':
      return AccountAccessManager.canManageAds()
    case 'surveys':
      return AccountAccessManager.canManageSurveys()
    case 'gallery':
      return AccountAccessManager.canSeeGallery()
    case 'earnings':
      return hasActiveProperties && AccountAccessManager.canSeeEarnings()
    case 'properties':
      return AccountAccessManager.canSeeProperties()
    case 'catalogs':
      return AccountAccessManager.canSeeCatalogs() && clientConfiguration.showCatalogs
    case 'audiences':
      return AccountAccessManager.canSeeAudiences() && clientConfiguration.showAudiences
    case 'balance':
      return AccountAccessManager.canSeeBalance()
    case 'settings':
      return AccountAccessManager.canSeeAccountInfo()
    case 'admin-info':
      return PlatformAccessManager.canManageInfos()
    case 'admin-users':
      return PlatformAccessManager.canManagePlatformUsers()
    case 'admin-industries':
      return PlatformAccessManager.canManageIndustries()
    case 'admin-configuration':
      return PlatformAccessManager.canManageConfiguration()
    case 'forecasting':
      return hasActiveProperties && AccountAccessManager.canSeeForecastings()
    case 'admin-dailycosts':
      return PlatformAccessManager.canSeeDailyCosts()
    case 'admin-performance-report':
      return PlatformAccessManager.canSeePerfomanceReport()
    default:
      return true
  }
}
